import { Box, Button, Flex, Link, Text, useToast } from '@chakra-ui/react';
import { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import {
  acceptSnapshotInvite,
  getJoinPreview,
  uploadPartnerSnapshot,
  type SnapshotJoinPreview,
} from 'services/snapshotsService';
import { SnapshotCsvMappingFlow } from './components/SnapshotCsvMappingFlow';
import { SnapshotCsvUpload } from './components/SnapshotCsvUpload';
import {
  SnapshotCard,
  SnapshotPageShell,
  SnapshotPageSpinner,
  SnapshotPrimaryButton,
  SnapshotStatusTag,
} from './components/snapshotUi';
import { getSnapshotApiErrorMessage } from './snapshotApiError';

export function SnapshotJoin() {
  const { token } = useAuth() || {};
  const { token: inviteToken } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const [preview, setPreview] = useState<SnapshotJoinPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [boundId, setBoundId] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [step, setStep] = useState<'upload' | 'mapping'>('upload');
  const [submitting, setSubmitting] = useState(false);

  const joinPath = inviteToken ? `/admin/snapshots/join/${inviteToken}` : '/admin/snapshots';
  const signUpUrl = `/auth/sign-up?redirect=${encodeURIComponent(joinPath)}`;
  const signInUrl = `/auth/sign-in?redirect=${encodeURIComponent(joinPath)}`;

  const loadPreview = useCallback(async () => {
    if (!inviteToken) return;
    setLoading(true);
    try {
      const data = await getJoinPreview(inviteToken);
      setPreview(data);
    } catch {
      toast({ title: 'Invalid or expired invite', status: 'error' });
    } finally {
      setLoading(false);
    }
  }, [inviteToken, toast]);

  useEffect(() => {
    loadPreview();
  }, [loadPreview]);

  useEffect(() => {
    if (!token || !inviteToken || boundId) return;
    acceptSnapshotInvite(token, inviteToken)
      .then((res) => setBoundId(res.snapshotId))
      .catch(() => {
        /* email mismatch: user sees message below */
      });
  }, [token, inviteToken, boundId]);

  const snapshotId = boundId || preview?.snapshotId;

  const handlePartnerUpload = async (mappings: {
    columnMapping: Record<string, string>;
    stageMapping: Record<string, 'lead' | 'opportunity' | 'customer'>;
  }) => {
    if (!token || !snapshotId || !file) return;
    setSubmitting(true);
    try {
      await uploadPartnerSnapshot(token, snapshotId, file, mappings);
      toast({
        title: 'Thanks',
        description: 'We are computing overlap. Check the snapshots list shortly.',
        status: 'success',
      });
      navigate('/admin/snapshots');
    } catch (err: unknown) {
      toast({
        title: getSnapshotApiErrorMessage(err, 'Upload failed'),
        status: 'error',
      });
      throw err;
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <SnapshotPageShell>
        <SnapshotPageSpinner />
      </SnapshotPageShell>
    );
  }

  if (!preview) {
    return (
      <SnapshotPageShell>
        <Text textAlign="center" py={20} color="gray.600">
          This invite link is not valid.
        </Text>
      </SnapshotPageShell>
    );
  }

  return (
    <SnapshotPageShell>
      <Box maxW="720px" mx="auto">
        <Text fontSize="sm" color="gray.500" mb={6}>
          cocorev
        </Text>
        <Text fontSize="md" color="gray.600" mb={6} lineHeight="1.6">
          <Text as="span" fontWeight="600" color="navy.700">
            {preview.initiatorCompanyName}
          </Text>{' '}
          invited{' '}
          <Text as="span" fontWeight="600" color="navy.700">
            {preview.partnerCompanyName}
          </Text>{' '}
          to share account lists on cocorev.
        </Text>

        <Text
          fontSize={{ base: '2xl', md: '3xl' }}
          fontWeight="700"
          color="navy.700"
          lineHeight="1.2"
          mb={4}
        >
          See the opportunities you and {preview.initiatorCompanyName} share
        </Text>
        <Text color="gray.500" fontSize="md" lineHeight="1.65" mb={5}>
          Upload a list of accounts and see where you overlap. No CRM needed. It&apos;s
          free and takes about a minute.
        </Text>

        <SnapshotStatusTag
          label={`${preview.initiatorCompanyName}'s list is ready`}
          bg="green.100"
          color="green.600"
        />

        {preview.uploadPrompt && (
          <Box mt={6} p={4} borderRadius="16px" bg="gray.50">
            <Text fontSize="sm" color="gray.600" lineHeight="1.55">
              <Text as="span" fontWeight="600" color="navy.700">What to upload: </Text>
              {preview.uploadPrompt}
            </Text>
          </Box>
        )}

        {!token ? (
          <SnapshotCard mt={8}>
            <Text mb={6} color="gray.600" lineHeight="1.6" fontSize="sm">
              Create a free cocorev account to add your list. We&apos;ll bring you right
              back here after sign-up.
            </Text>
            <Button as={RouterLink} to={signUpUrl} variant="brand" w="100%" mb={4}>
              Add your list
            </Button>
            <Text fontSize="sm" color="gray.500" textAlign="center">
              Already on cocorev?{' '}
              <Link as={RouterLink} to={signInUrl} fontWeight="600" color="navy.700">
                Sign in
              </Link>
            </Text>
          </SnapshotCard>
        ) : step === 'mapping' && file ? (
          <Box mt={8}>
            <SnapshotCsvMappingFlow
              token={token}
              file={file}
              submitLabel="Add your list"
              isSubmitting={submitting}
              onBack={() => setStep('upload')}
              onSubmit={handlePartnerUpload}
            />
          </Box>
        ) : (
          <SnapshotCard mt={8}>
            <SnapshotCsvUpload file={file} onFileChange={setFile} />
            <Flex justify="flex-end" mt={6}>
              <SnapshotPrimaryButton
                isDisabled={!file}
                onClick={() => {
                  if (!file) return;
                  setStep('mapping');
                }}
              >
                Continue
              </SnapshotPrimaryButton>
            </Flex>
          </SnapshotCard>
        )}
      </Box>
    </SnapshotPageShell>
  );
}
